import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown/with-html';
import { useTranslation } from '../../../../hooks/useTranslation';
import { CustomDialog } from '../common/custom-dialog';
import HelpOutlineIcon from '../icons/HelpIcon';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  makeStyles,
  Typography,
  Theme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import colors from '../../../../themes/main-colors';
import PgeVideoPlayer from '../../../pge-video-player';
import { useIsMobile } from '../../../../util/style-utils';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    padding: '1.5rem',
  },
  content: {
    scrollbarWidth: 'none',
    overflowY: 'auto',
    height: '600px',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
    },
  },
  accordionRoot: {
    boxShadow: 'none',
    marginBottom: '1rem',
    '&:before': {
      display: 'none',
    },
    '&:last-child': {
      marginBottom: 0,
    },
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: '16px',
  },
  font16: {
    fontSize: 16,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      margin: '3px 0px',
    },
  },
  accordionDetails: {
    display: 'block',
  },
  answerStyles: {
    fontSize: '16px',
    margin: '15px',
  },
  helpIcon: {
    fontSize: 30,
    color: colors.white,
  },
  videoBox: {
    background: colors.white,
    padding: '0.5rem 1.5rem',
  },
  font20: {
    fontSize: 20,
  },
}));

interface PSPSFAQCompProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const PSPSFAQComp = ({ open, setOpen }: PSPSFAQCompProps) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();

  const handleClose = () => {
    setOpen(false);
  };
  const outageFaqs = useMemo(
    () => [
      {
        question: t('PSPS_FAQ_Q1'),
        answer: richT('PSPS_FAQ_A1'),
      },
      {
        question: t('PSPS_FAQ_Q2'),
        answer: richT('PSPS_FAQ_A2'),
      },
      {
        question: t('PSPS_FAQ_Q3'),
        answer: richT('PSPS_FAQ_A3'),
      },
      {
        question: t('PSPS_FAQ_Q4'),
        answer: richT('PSPS_FAQ_A4'),
      },
      {
        question: t('PSPS_FAQ_Q5'),
        answer: (
          <ReactMarkdown
            source={t('PSPS_FAQ_A5')}
            escapeHtml={false}
            linkTarget={'_blank'}
          />
        ),
      },
    ],
    [],
  );

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={t('PSPS_FAQ_TITLE')}
      titleIcon={<HelpOutlineIcon className={classes.helpIcon} />}
    >
      <Box className={classes.content}>
        <div className={classes.videoBox}>
          <Typography>
            <p className={clsx(classes.accordionTitle, classes.font20)}>
              {t('PSPS_FAQ_HEADER')}
            </p>
          </Typography>
          <PgeVideoPlayer
            video={{
              url: 'https://www.youtube.com/watch?v=oHLoVog_iDU',
              width: '-webkit-fill-available',
              height: isMobile ? '209px' : '292px',
            }}
          />
          <Typography>
            <p className={classes.font16}>
              <ReactMarkdown
                source={t('PSPS_FAQ_INFO')}
                escapeHtml={false}
                linkTarget={'_blank'}
              />
            </p>
          </Typography>
        </div>
        <Box className={classes.dialogContent}>
          {outageFaqs.map((faq, idx) => (
            <Accordion key={idx} className={classes.accordionRoot}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Typography>
                  <p className={classes.accordionTitle}>{faq.question}</p>
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Divider />
                <p className={classes.answerStyles}>{faq.answer}</p>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </CustomDialog>
  );
};
